import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Briefcase, ExternalLink } from 'lucide-react';
import type { Source } from '@/lib/types';

interface MoreInfoSheetProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  sources?: Source[];
}

export function MoreInfoSheet({ 
  children, 
  open, 
  onOpenChange,
  sources = []
}: MoreInfoSheetProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [profession, setProfession] = useState('');

  const handleSearch = () => {
    console.log('Searching with:', { searchQuery, location, date, profession });
    // In a real app, this would filter/refine the search results
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        {children || (
          <button className="hidden">
            Open
          </button>
        )}
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[75vh] rounded-t-[32px] border-t-0 bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.04)] p-0">
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-200 rounded-full opacity-50" />
        </div>
        
        <div className="h-full overflow-y-auto px-6 pb-8 space-y-4">
          <SheetHeader>
            <SheetTitle className="text-left">
              {sources.length > 0 ? `${sources.length} Sources` : 'Refine Search'}
            </SheetTitle>
          </SheetHeader>

          {/* Sources List */}
          {sources.length > 0 && (
            <div className="space-y-2 mb-6">
              {sources.map((source, index) => (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{source.title}</p>
                    <p className="text-xs text-gray-500 truncate">{source.url}</p>
                    <p className="text-xs text-blue-600 mt-1">{source.relevance}</p>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Divider if sources exist */}
          {sources.length > 0 && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Refine your search</p>
            </div>
          )}

          {/* Main Search Input */}
          <div className="bg-gray-100 rounded-none px-5 py-4">
             <Input
                type="text"
                placeholder="Search here..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 bg-transparent p-0 h-auto text-base placeholder:text-gray-400 focus-visible:ring-0 text-gray-900 shadow-none rounded-none"
              />
          </div>

          <div className="grid grid-cols-2 gap-3">
              {/* Location */}
              <div className="flex items-center gap-3 px-4 py-4 bg-gray-100 rounded-none">
                <MapPin className="w-5 h-5 text-gray-400 shrink-0" />
                <Input
                  type="text"
                  placeholder="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="border-0 bg-transparent p-0 h-auto text-sm placeholder:text-gray-400 focus-visible:ring-0 text-gray-900 min-w-0 shadow-none rounded-none"
                />
              </div>

              {/* Date */}
              <div className="flex items-center gap-3 px-4 py-4 bg-gray-100 rounded-none">
                <Calendar className="w-5 h-5 text-gray-400 shrink-0" />
                <Input
                  type="text"
                  placeholder="DD/MM/..."
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border-0 bg-transparent p-0 h-auto text-sm placeholder:text-gray-400 focus-visible:ring-0 text-gray-900 min-w-0 shadow-none rounded-none"
                />
              </div>
          </div>

          {/* Professional Background */}
          <div className="flex items-center gap-3 px-5 py-4 bg-gray-100 rounded-none">
            <Briefcase className="w-5 h-5 text-gray-400 shrink-0" />
            <Input
              type="text"
              placeholder="Professional background"
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              className="border-0 bg-transparent p-0 h-auto text-base placeholder:text-gray-400 focus-visible:ring-0 text-gray-900 shadow-none rounded-none"
            />
          </div>

          <div className="pt-4">
              {/* Search Button */}
              <Button
                onClick={handleSearch}
                className="w-full h-14 bg-gray-200 hover:bg-gray-300 text-gray-400 text-lg font-medium rounded-full shadow-none data-[active=true]:bg-black data-[active=true]:text-white transition-colors"
                data-active={!!searchQuery || !!location || !!date || !!profession}
              >
                Search Again
              </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
